import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Search } from "lucide-react";
import { api } from "../lib/api";
import { EventCard, VenueCard, ArtistCard, AttractionCard, ActivityCard, BusinessCard, GigCard } from "../components/Cards";
import { SpotlightCard } from "./BarDad";
import { DealGridCard } from "./Deals";
import { JobCard } from "./Hiring";
import { ProductCard } from "./Marketplace";
import { useSeo } from "../components/VIP";
import { useSuggestSearch, SuggestDropdown, useOutsideClose } from "../components/SearchSuggestions";

const GROUPS = [
  { key: "events", label: "Events", href: "/events", render: (e) => <EventCard key={e.id} e={e} /> },
  { key: "venues", label: "Venues", href: "/venues", render: (v) => <VenueCard key={v.id} v={v} /> },
  { key: "artists", label: "Artists", href: "/artists", render: (a) => <ArtistCard key={a.id} a={a} /> },
  { key: "attractions", label: "Attractions", href: "/attractions", render: (a) => <AttractionCard key={a.id} a={a} /> },
  { key: "activities", label: "Activities", href: "/activities", render: (a) => <ActivityCard key={a.id} a={a} /> },
  { key: "businesses", label: "Businesses", href: "/businesses", render: (b) => <BusinessCard key={b.id} b={b} /> },
  { key: "deals", label: "Deals", href: "/deals", render: (d) => <DealGridCard key={d.id} d={d} /> },
  { key: "gigs", label: "Gigs", href: "/gigs", render: (g) => <GigCard key={g.id} g={g} /> },
  { key: "hiring_jobs", label: "Jobs & Hiring", href: "/hiring", render: (j) => <JobCard key={j.id} j={j} /> },
  { key: "products", label: "Marketplace", href: "/marketplace", render: (p) => <ProductCard key={p.id} p={p} /> },
  { key: "bar_dad", label: "Bar Dad Spotlight", href: "/bar-dad", render: (s) => <SpotlightCard key={s.id} s={s} /> },
  { key: "kb", label: "Knowledge Base", href: "/knowledge-base", render: (a) => (
    <Link key={a.id} to={`/knowledge-base/${a.slug}`} className="bg-[#111] border border-white/10 hover:border-rose-500/40 rounded-2xl p-5 block transition-colors" data-testid={`search-kb-${a.slug}`}>
      <p className="overline !text-[10px] mb-1">{a.category || "Guide"}</p>
      <h3 className="font-display font-bold">{a.title}</h3>
      {a.summary && <p className="text-sm text-zinc-400 mt-2 line-clamp-3">{a.summary}</p>}
    </Link>
  ) },
];

export default function SearchResults() {
  const [params, setParams] = useSearchParams();
  const urlQ = params.get("q") || "";
  const [q, setQ] = useState(urlQ);
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [focused, setFocused] = useState(false);
  const nav = useNavigate();
  const wrapRef = useRef(null);
  const { loading: sLoading, items: sItems } = useSuggestSearch(q);
  useOutsideClose(wrapRef, () => setFocused(false));
  useSeo(`Search${urlQ ? ` "${urlQ}"` : ""} | Gulf Coast VIP`, "Search events, venues, artists, attractions, activities and Bar Dad spotlights across the Gulf Coast.");

  useEffect(() => { setQ(urlQ); }, [urlQ]);

  useEffect(() => {
    if (!urlQ.trim()) { setResults(null); return; }
    setLoading(true);
    api.get(`/search?q=${encodeURIComponent(urlQ)}`)
      .then((r) => setResults(r.data))
      .catch(() => setResults(null))
      .finally(() => setLoading(false));
  }, [urlQ]);

  const total = results ? GROUPS.reduce((n, g) => n + (results[g.key] || []).length, 0) : 0;
  const doSubmit = () => { const t = q.trim(); if (t) { setFocused(false); setParams({ q: t }); } };
  const goPath = (p) => { setFocused(false); setQ(""); nav(p); };

  return (
    <div className="px-6 md:px-12 lg:px-20 py-12 min-h-[60vh]" data-testid="search-page">
      <p className="overline mb-2">Universal search</p>
      <h1 className="font-display font-black text-4xl sm:text-5xl mb-8">Search</h1>
      <div ref={wrapRef} className="relative max-w-2xl" data-testid="search-page-wrap">
        <form onSubmit={(e) => { e.preventDefault(); doSubmit(); }}
          className="flex items-center gap-2 bg-[#111] border border-white/10 rounded-xl px-4 py-3 focus-within:border-rose-500">
          <Search size={18} className="text-zinc-500" />
          <input value={q} onChange={(e) => setQ(e.target.value)} onFocus={() => setFocused(true)} autoFocus autoComplete="off"
            placeholder="Search events, venues, artists, attractions, activities..."
            className="bg-transparent outline-none w-full" data-testid="search-page-input" />
          <button className="bg-rose-500 hover:bg-rose-600 font-bold text-sm px-5 py-2 rounded-lg shrink-0" data-testid="search-page-btn">Search</button>
        </form>
        {focused && (
          <SuggestDropdown
            query={q}
            items={sItems}
            loading={sLoading}
            onPick={goPath}
            onSeeAll={doSubmit}
            className="absolute top-full left-0 right-0 mt-2"
            testidPrefix="search-page"
          />
        )}
      </div>

      {loading && <p className="text-zinc-500 mt-10">Searching…</p>}
      {!loading && results && (
        <p className="text-sm text-zinc-500 mt-6" data-testid="search-total">
          {total} result{total === 1 ? "" : "s"} for “{urlQ}”
        </p>
      )}
      {!loading && results && total === 0 && (
        <p className="text-zinc-400 mt-10" data-testid="search-empty">Nothing found — try a different name, city or type.</p>
      )}
      {!loading && results && GROUPS.map((g) => {
        const items = results[g.key] || [];
        if (!items.length) return null;
        return (
          <section key={g.key} className="mt-12" data-testid={`search-group-${g.key}`}>
            <div className="flex items-baseline justify-between mb-5">
              <h2 className="font-display font-bold text-2xl">{g.label} <span className="text-zinc-500 text-base font-normal">({items.length})</span></h2>
              <Link to={g.href} className="text-sm text-rose-400 hover:underline">Browse all</Link>
            </div>
            <div className="grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5">{items.map(g.render)}</div>
          </section>
        );
      })}
      {!urlQ && !loading && <p className="text-zinc-500 mt-10">Type something above to search the whole directory.</p>}
    </div>
  );
}
